// src/merch/ProductViewer.jsx
//
// 3D preview. Model products (sticks) load the real .glb: `Base` gets the
// inventory colour, each print_<part>_<face> mesh shows its zone's design.
// The mesh UVs are remapped from the model's island into that zone's
// print area of its composite (same transform extract_zones.py measured),
// so the 3D view matches the flat editor exactly.
// The puck is still placeholder geometry.

import React, { Suspense, useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { printAreaFor } from "./products";
import { useLiveTexture } from "./liveTexture";

/* ---------------- shared ---------------- */

function useCanvasTexture(canvas, { flipY = true } = {}) {
  const tex = useMemo(() => {
    const t = new THREE.CanvasTexture(document.createElement("canvas"));
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 8;
    t.flipY = flipY;
    return t;
  }, [flipY]);
  useEffect(() => {
    if (!canvas) return;
    tex.image = canvas;
    tex.needsUpdate = true;
  }, [canvas, tex]);
  return tex;
}

// Flies the camera to the default front / back view whenever `viewKey`
// changes (every Front/Back click), then lets go — orbiting stays free.
// The model itself never rotates, so it can't fight the orbit controls.
const VIEW_DIST = 1.45, VIEW_LIFT = 0.05;
/** frame: { center: Vector3, size: Vector3 } of what to fit (model units). */
function CameraFly({ back, viewKey, frame }) {
  const { camera, controls, size: vp } = useThree();
  const anim = useRef(null);
  useEffect(() => {
    let target = new THREE.Vector3(0, 0, 0);
    let dist = VIEW_DIST;
    if (frame) {
      target = frame.center.clone();
      const vFov = (camera.fov * Math.PI) / 180;
      const aspect = vp.width / Math.max(1, vp.height);
      const fitH = frame.size.y / 2 / Math.tan(vFov / 2);
      const fitW = frame.size.x / 2 / (Math.tan(vFov / 2) * aspect);
      dist = Math.min(VIEW_DIST, Math.max(fitH, fitW) * 1.35 + frame.size.z / 2);
    }
    anim.current = {
      t: 0,
      fromPos: camera.position.clone(),
      toPos: target.clone().add(new THREE.Vector3(0, VIEW_LIFT * dist, back ? -dist : dist)),
      fromTarget: controls ? controls.target.clone() : new THREE.Vector3(),
      toTarget: target,
    };
  }, [viewKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useFrame((_, dt) => {
    const a = anim.current;
    if (!a) return;
    a.t = Math.min(1, a.t + dt / 0.6);
    const e = a.t < 0.5 ? 4 * a.t * a.t * a.t : 1 - Math.pow(-2 * a.t + 2, 3) / 2; // easeInOutCubic
    // swing around the side (not through the stick) when switching faces
    // orbit around the (moving) target so a front↔back switch swings round
    const tgt = a.fromTarget.clone().lerp(a.toTarget, e);
    const fromRel = a.fromPos.clone().sub(a.fromTarget), toRel = a.toPos.clone().sub(a.toTarget);
    const rel = fromRel.clone().lerp(toRel, e);
    const r = fromRel.length() * (1 - e) + toRel.length() * e;
    if (rel.lengthSq() > 1e-6) rel.setLength(Math.max(r, 0.1));
    camera.position.copy(tgt.clone().add(rel));
    if (controls) {
      controls.target.copy(tgt);
      controls.update();
    } else {
      camera.lookAt(tgt);
    }
    if (a.t >= 1) anim.current = null;
  });
  return null;
}

/* ---------------- model products (.glb) ---------------- */

/** Remap a print mesh's UVs: model island → zone print area (glTF v-down;
 *  textures use flipY = false so v=0 is the canvas top). */
function remapUVs(geometry, zone) {
  const g = geometry.clone();
  const uv = g.attributes.uv;
  if (!uv) return g;
  const { cx, cy, angle, a0, b0, a1, b1 } = zone.uv;
  const area = printAreaFor(zone.sticker);
  const aw = area.x1 - area.x0, ah = area.y1 - area.y0;
  const t = (angle * Math.PI) / 180, cs = Math.cos(t), sn = Math.sin(t);
  const out = new Float32Array(uv.count * 2);
  for (let i = 0; i < uv.count; i++) {
    const x = uv.getX(i) - cx, y = uv.getY(i) - cy;
    const px = cs * x - sn * y, py = sn * x + cs * y;
    let s = (px - a0) / (a1 - a0);
    const tt = (py - b0) / (b1 - b0);
    // left-handed: the model is mirrored, so flip across the sticker too —
    // designs stay readable and land on the mirrored sticker shape
    if (zone.mirror) s = 1 - s;
    out[i * 2] = area.x0 + s * aw;
    out[i * 2 + 1] = area.y0 + tt * ah;
  }
  g.setAttribute("uv", new THREE.BufferAttribute(out, 2));
  return g;
}

function ZonePrint({ mesh, zone, canvas }) {
  const tex = useCanvasTexture(canvas, { flipY: false });
  useLiveTexture(zone.id, tex); // realtime while dragging in the editor
  const mat = useMemo(
    () => new THREE.MeshStandardMaterial({
      map: tex, transparent: true, alphaTest: 0.02, roughness: 0.45, metalness: 0,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
    }),
    [tex]
  );
  return (
    <mesh
      geometry={mesh.geometry}
      material={mat}
      position={mesh.position}
      quaternion={mesh.quaternion}
      scale={mesh.scale}
      visible={!!canvas}
    />
  );
}

function ModelProduct({ url, zones, colour, composites, activeZone, back, viewKey }) {
  const gltf = useGLTF(url);
  const { scene } = gltf;
  // zones arrive as a fresh array every render; rebuild geometry only when
  // they actually change (ids / hand)
  const zonesKey = zones.map((z) => z.id + (z.mirror ? ":L" : "")).join("|");

  // Split the model once: tinted body parts, print meshes, everything else hidden.
  const parts = useMemo(() => {
    scene.updateMatrixWorld(true);
    const body = [];
    const prints = [];
    const box = new THREE.Box3();
    scene.traverse((o) => {
      if (!o.isMesh) return;
      const names = [];
      for (let n = o; n; n = n.parent) names.push(n.name || "");
      const printName = names.find((n) => n.startsWith("print_"));
      // leftovers from modelling (e.g. an unused Cube.001 or reference plane)
      // stay out; everything else (body, string / hanger, caps) is shown.
      // Only OBJECT names count: a multi-material object's parts are named
      // after its mesh data ("Plane007", "Cube003") and must never be hidden.
      // (three.js also strips the dot: "Cube.001" arrives as "Cube001".)
      const assoc = gltf.parser?.associations;
      const objectNames = [];
      for (let n = o; n; n = n.parent) {
        const a = assoc?.get(n);
        if (a ? a.nodes !== undefined : n !== o || !o.parent?.isGroup) objectNames.push(n.name || "");
      }
      const leftover = objectNames.some((n) => /^(cube|plane|reference|empty|cylinder)([._]?\d+)?$/i.test(n));
      if (!printName && leftover) return;
      // bake world transform so we can render flat under our own group
      const geom = o.geometry.clone();
      geom.applyMatrix4(o.matrixWorld);
      if (printName) {
        const zone = zones.find((z) => z.mesh === printName);
        if (!zone) return;
        prints.push({ zone, mesh: { geometry: remapUVs(geom, zone), position: [0, 0, 0], quaternion: new THREE.Quaternion(), scale: [1, 1, 1] } });
      } else {
        const m = o.material.clone();
        body.push({ geometry: geom, material: m, tint: /^base(\.\d+)?$/i.test(o.material.name || "")  /* Base, Base.001… */ });
        geom.computeBoundingBox();
        box.union(geom.boundingBox);
      }
    });
    const size = new THREE.Vector3(); box.getSize(size);
    const center = new THREE.Vector3(); box.getCenter(center);
    const scale = 1 / Math.max(size.x, size.y, size.z, 1e-6);
    const mirror = zones.some((z) => z.mirror);
    // framing box per zone, in displayed space: (p − center) · scale
    const frames = {};
    for (const p of prints) {
      p.mesh.geometry.computeBoundingBox();
      const b = p.mesh.geometry.boundingBox;
      const c = new THREE.Vector3(); b.getCenter(c);
      const sz = new THREE.Vector3(); b.getSize(sz);
      const fc = c.sub(center).multiplyScalar(scale);
      if (mirror) fc.x = -fc.x;
      frames[p.zone.id] = { center: fc, size: sz.multiplyScalar(scale) };
    }
    return { body, prints, scale, center, frames, mirror };
  }, [scene, zonesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // inventory colour on the Base material (other materials, e.g. Cap, keep theirs)
  useEffect(() => {
    for (const b of parts.body) {
      if (b.tint) { b.material.color.set(colour.hex); b.material.needsUpdate = true; }
    }
  }, [parts, colour]);

  const c = parts.center;
  return (
    <group>
      <CameraFly back={back} viewKey={`${back ? "b" : "f"}-${activeZone}-${viewKey}`} frame={parts.frames[activeZone]} />
      <group scale={[parts.mirror ? -parts.scale : parts.scale, parts.scale, parts.scale]}>
        <group position={[-c.x, -c.y, -c.z]}>
          {parts.body.map((b, i) => (
            <mesh key={i} geometry={b.geometry} material={b.material} castShadow />
          ))}
          {parts.prints.map((p) => (
            <ZonePrint key={p.zone.id} mesh={p.mesh} zone={p.zone} canvas={composites[p.zone.id] || null} />
          ))}
        </group>
      </group>
    </group>
  );
}

/* ---------------- placeholder products ---------------- */

function mapUVsToArea(geom, area) {
  const uv = geom.attributes.uv;
  for (let i = 0; i < uv.count; i++) {
    const u = uv.getX(i), v = uv.getY(i);
    uv.setXY(i, area.x0 + u * (area.x1 - area.x0), 1 - area.y1 + v * (area.y1 - area.y0));
  }
  uv.needsUpdate = true;
  return geom;
}

function Puck({ colour, area, canvas, zoneId }) {
  const texture = useCanvasTexture(canvas);
  useLiveTexture(zoneId, texture);
  const r = 0.12, h = 0.08;
  const printGeom = useMemo(() => mapUVsToArea(new THREE.CircleGeometry(r * 0.96, 96), area), [area]);
  return (
    <group rotation={[0.9, 0, 0]}>
      <mesh castShadow>
        <cylinderGeometry args={[r, r, h, 96]} />
        <meshStandardMaterial color={colour.hex} roughness={0.8} />
      </mesh>
      <mesh geometry={printGeom} rotation={[-Math.PI / 2, 0, 0]} position={[0, h / 2 + 0.0006, 0]}>
        <meshStandardMaterial map={texture} transparent alphaTest={0.02} roughness={0.45} polygonOffset polygonOffsetFactor={-2} />
      </mesh>
    </group>
  );
}

/* die-cut sticker: a thin vinyl piece in the live contour shape */
const MM = 0.0032; // scene units per mm (a 3" sticker ≈ the puck's size)

function stickerShapes(sticker) {
  const W = sticker.widthMm, H = sticker.heightMm;
  const toV = ([x, y]) => new THREE.Vector2((x - W / 2) * MM, (H / 2 - y) * MM);
  if (sticker.cutRings?.length) {
    return sticker.cutRings.map((r) => new THREE.Shape(r.map(toV)));
  }
  // no art yet: the max-size rounded square
  const r = Math.min(W, H) * 0.12, s = new THREE.Shape();
  const x0 = -W / 2 * MM, y0 = -H / 2 * MM, x1 = W / 2 * MM, y1 = H / 2 * MM, R = r * MM;
  s.moveTo(x0 + R, y0); s.lineTo(x1 - R, y0); s.quadraticCurveTo(x1, y0, x1, y0 + R);
  s.lineTo(x1, y1 - R); s.quadraticCurveTo(x1, y1, x1 - R, y1); s.lineTo(x0 + R, y1);
  s.quadraticCurveTo(x0, y1, x0, y1 - R); s.lineTo(x0, y0 + R); s.quadraticCurveTo(x0, y0, x0 + R, y0);
  return [s];
}

function DieCutSticker({ colour, sticker, canvas, zoneId }) {
  const texture = useCanvasTexture(canvas);
  useLiveTexture(zoneId, texture);
  const area = printAreaFor(sticker);
  const key = JSON.stringify(sticker.cutRings || null) + sticker.widthMm;
  const { body, face } = useMemo(() => {
    const shapes = stickerShapes(sticker);
    const body = new THREE.ExtrudeGeometry(shapes, { depth: 0.0012, bevelEnabled: true, bevelThickness: 0.0003, bevelSize: 0.0004, bevelSegments: 2, curveSegments: 8 });
    const face = new THREE.ShapeGeometry(shapes, 8);
    // UVs: shape position (mm) → the sticker's spot in the design square
    const pos = face.attributes.position, uv = face.attributes.uv;
    const W = sticker.widthMm, H = sticker.heightMm, aw = area.x1 - area.x0, ah = area.y1 - area.y0;
    for (let i = 0; i < pos.count; i++) {
      const xm = pos.getX(i) / MM + W / 2, ym = H / 2 - pos.getY(i) / MM;
      uv.setXY(i, area.x0 + (xm / W) * aw, 1 - (area.y0 + (ym / H) * ah));
    }
    uv.needsUpdate = true;
    return { body, face };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { body.dispose(); face.dispose(); }, [body, face]);
  return (
    <group rotation={[-0.35, 0.25, 0]}>
      <mesh geometry={body} castShadow>
        <meshStandardMaterial color={colour.hex} roughness={0.35} metalness={0.02} />
      </mesh>
      <mesh geometry={face} position={[0, 0, 0.0012 + 0.0004]}>
        <meshStandardMaterial map={texture} transparent alphaTest={0.02} roughness={0.35} polygonOffset polygonOffsetFactor={-2} />
      </mesh>
    </group>
  );
}

/* ---------------- viewer ---------------- */

export default function ProductViewer({ product, colour, zones, composites, activeZone, viewKey = 0, onContextRestored }) {
  const isModel = !!product.model?.glb;
  const zone = zones.find((z) => z.id === activeZone) || zones[0];
  const back = zone?.face === "back";
  return (
    <Canvas
      camera={{ position: isModel ? [0, 0.05, 1.45] : [0, 0.1, 0.45], fov: 35 }}
      dpr={[1, 2]}
      style={{ width: "100%", height: "100%" }}
      onCreated={({ gl }) => {
        // Phones / tablets can drop the GPU context under memory pressure:
        // the model comes back but its art textures are blank. Ask to have
        // the context back, then let the app rebuild the view.
        const el = gl.domElement;
        el.addEventListener("webglcontextlost", (e) => e.preventDefault());
        el.addEventListener("webglcontextrestored", () => onContextRestored?.());
      }}
    >
      {/* transparent: the card behind draws the studio grid + glow */}
      <ambientLight intensity={0.75} />
      <directionalLight position={[2, 3, 4]} intensity={1.4} />
      <directionalLight position={[-3, 1, -2]} intensity={0.6} />
      <directionalLight position={[0, -2, -3]} intensity={0.35} />
      {isModel ? (
        <Suspense fallback={null}>
          <ModelProduct
            url={product.model.glb} zones={zones} colour={colour} composites={composites}
            activeZone={zone?.id} back={back} viewKey={viewKey}
          />
        </Suspense>
      ) : product.model?.type === "sticker" ? (
        <DieCutSticker colour={colour} sticker={zone.sticker} canvas={composites[zone.id] || null} zoneId={zone.id} />
      ) : (
        <Puck colour={colour} area={printAreaFor(zone.sticker)} canvas={composites[zone.id] || null} zoneId={zone.id} />
      )}
      {/* drag = orbit · right-drag / two-finger drag = pan · scroll / pinch = zoom */}
      <OrbitControls makeDefault enablePan screenSpacePanning minDistance={0.15} maxDistance={3} target={[0, 0, 0]} />
    </Canvas>
  );
}
